import { fetchConfig } from '@theme/utilities';

const STORAGE_KEY = 'pupmd_campaign_v1';
const BLOCK_START = '[PupMD campaign]';
const BLOCK_END = '[/PupMD campaign]';
const BLOCK_RE = /\[PupMD campaign\][\s\S]*?\[\/PupMD campaign\]/;
const BLOCK_STRIP_RE = /(?:\n\n)?\[PupMD campaign\][\s\S]*?\[\/PupMD campaign\]/g;
const MAX_NAME = 64;
const MAX_VALUE = 200;
const NETWORK_TIMEOUT_MS = 3000;

/** @type {Record<string, string> | null} */
let campaign = null;
/** @type {string | null} */
let campaignBlock = null;
/** @type {string | null} */
let fallbackBlock = null;
/** @type {Promise<void>} */
let writeQueue = Promise.resolve();
/** @type {AbortController | null} */
let formGuardController = null;
let formGuardOwners = 0;
/** @type {WeakMap<HTMLFormElement, string>} */
const latestNoteByForm = new WeakMap();

/**
 * @param {Iterable<[string, string]>} entries
 * @returns {Record<string, string> | null}
 */
function parseEntries(entries) {
  /** @type {Record<string, string>} */
  const next = {};
  for (const [rawName, rawValue] of entries) {
    if (typeof rawValue !== 'string') continue;
    const name = rawName.toLowerCase();
    if (name !== 'tid' && (!/^utm_[a-z0-9_]+$/.test(name) || name.length > MAX_NAME)) continue;
    let value = rawValue
      .replace(/[\r\n]/g, ' ')
      .replace(/\[\s*\/?\s*PupMD\s+campaign\s*\]/gi, '')
      .trim();
    if (value.length > MAX_VALUE) value = value.slice(0, MAX_VALUE).trim();
    if (value) next[name] = value;
  }
  return Object.keys(next).length > 0 ? next : null;
}

/** @param {string} search */
function parseSearch(search) {
  const query = search.startsWith('?') ? search.slice(1) : search;
  return parseEntries(new URLSearchParams(query).entries());
}

/** @param {unknown} value */
function parseStored(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  /** @type {[string, string][]} */
  const entries = [];
  for (const [key, entryValue] of Object.entries(value)) {
    if (typeof entryValue === 'string') entries.push([key, entryValue]);
  }
  return parseEntries(entries);
}

/** @param {Record<string, string>} record */
function formatBlock(record) {
  const lines = Object.keys(record).map((name) => `${name}=${record[name]}`);
  return `${BLOCK_START}\n${lines.join('\n')}\n${BLOCK_END}`;
}

/** @param {string | null | undefined} note */
function extractBlock(note) {
  if (typeof note !== 'string' || !note) return null;
  const match = note.match(BLOCK_RE);
  return match ? match[0] : null;
}

/** @param {string | null | undefined} note */
function stripBlocks(note) {
  if (typeof note !== 'string' || !note) return '';
  return note.replace(BLOCK_STRIP_RE, '');
}

/**
 * @param {string} customerText
 * @param {string | null} block
 */
function composeNote(customerText, block) {
  if (!block) return customerText;
  if (!customerText) return block;
  return `${customerText}\n\n${block}`;
}

function readStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return parseStored(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** @param {Record<string, string>} record */
function persist(record) {
  campaign = record;
  campaignBlock = formatBlock(record);
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // memory only
  }
}

{
  const fromUrl = parseSearch(window.location.search || '');
  if (fromUrl) {
    persist(fromUrl);
  } else {
    campaign = readStorage();
    if (campaign) campaignBlock = formatBlock(campaign);
  }
}

/** @param {string} url */
function withJs(url) {
  return /\.js$/i.test(url) ? url : `${url.replace(/\/$/, '')}.js`;
}

/**
 * @param {string} url
 * @param {RequestInit} [init]
 */
async function fetchJson(url, init = {}) {
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), NETWORK_TIMEOUT_MS);
  const outer = init.signal;
  /** @param {Event} _event */
  const onOuterAbort = (_event) => timeout.abort();

  if (outer) {
    if (outer.aborted) timeout.abort();
    else outer.addEventListener('abort', onOuterAbort, { once: true });
  }

  try {
    const response = await fetch(url, { ...init, signal: timeout.signal });
    const data = await Promise.race([
      response.json(),
      new Promise((_, reject) => {
        const fail = () => {
          const error = new Error('Aborted');
          error.name = 'AbortError';
          reject(error);
        };
        if (timeout.signal.aborted) fail();
        else timeout.signal.addEventListener('abort', fail, { once: true });
      }),
    ]);
    return { ok: response.ok, status: response.status, data };
  } finally {
    clearTimeout(timer);
    if (outer) outer.removeEventListener('abort', onOuterAbort);
  }
}

/**
 * @param {{ customerText?: string, signal?: AbortSignal }} [options]
 */
function enqueueNoteUpdate(options = {}) {
  /** @returns {Promise<void>} */
  const task = async () => {
    const result = await fetchJson(withJs(Theme.routes.cart_url), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: options.signal,
    });

    if (!result.ok || !result.data || typeof result.data !== 'object') {
      if (options.customerText !== undefined) throw new Error('cart note read failed');
      return;
    }

    const cart = /** @type {{ note?: string | null }} */ (result.data);
    const existing = typeof cart.note === 'string' ? cart.note : '';
    const serverBlock = extractBlock(existing);
    if (!campaignBlock && serverBlock) fallbackBlock = serverBlock;

    const customerText =
      options.customerText !== undefined ? stripBlocks(options.customerText) : stripBlocks(existing);
    const block = campaignBlock || serverBlock || fallbackBlock;
    if (!campaignBlock && block) fallbackBlock = block;

    const nextNote = composeNote(customerText, block);
    if (nextNote === existing) return;

    const body = JSON.stringify({ note: nextNote });
    const config = fetchConfig('json', { body });
    const posted = await fetchJson(withJs(Theme.routes.cart_update_url), {
      ...config,
      signal: options.signal,
    });
    if (!posted.ok) throw new Error(`cart note update failed: ${posted.status}`);
  };

  const run = writeQueue.then(task, task);
  writeQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

export async function prepareCartAttribution() {
  if (!campaign) return;
  try {
    await enqueueNoteUpdate();
  } catch {
    // do not block add-to-cart
  }
}

/**
 * @param {string} enteredNote
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function saveCustomerCartNote(enteredNote, options = {}) {
  await enqueueNoteUpdate({
    customerText: stripBlocks(enteredNote),
    signal: options.signal,
  });
}

function seedFallbackBlockFromDom() {
  if (campaignBlock) return;
  for (const node of document.querySelectorAll('textarea[name="note"], #cart-note')) {
    if (node instanceof HTMLTextAreaElement) {
      const block = extractBlock(node.value);
      if (block) fallbackBlock = block;
    }
  }
}

/** @param {string} enteredNote */
function mergeNoteForSubmit(enteredNote) {
  return composeNote(stripBlocks(enteredNote), campaignBlock || fallbackBlock || extractBlock(enteredNote));
}

/** @param {Event} event */
function onCartNoteInput(event) {
  const target = event.target;
  if (!(target instanceof HTMLTextAreaElement) || target.name !== 'note') return;
  const form = target.form;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  latestNoteByForm.set(form, target.value);
}

/** @param {Event} event */
function onCartFormData(event) {
  if (!(event instanceof FormDataEvent)) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

  const formData = event.formData;
  if (!formData.has('note')) return;

  const remembered = latestNoteByForm.get(form);
  const entered = remembered !== undefined ? remembered : String(formData.get('note') ?? '');
  formData.set('note', mergeNoteForSubmit(entered));
}

export function bindCartNoteFormProtection() {
  seedFallbackBlockFromDom();
  formGuardOwners += 1;

  if (!formGuardController || formGuardController.signal.aborted) {
    formGuardController = new AbortController();
    const { signal } = formGuardController;
    document.addEventListener('input', onCartNoteInput, { signal });
    document.addEventListener('formdata', onCartFormData, { signal });
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    formGuardOwners = Math.max(0, formGuardOwners - 1);
    if (formGuardOwners === 0 && formGuardController) {
      formGuardController.abort();
      formGuardController = null;
    }
  };
}
